import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/* PrintMenu moved to CalcShell — now universal for all calculators */

/* ==========================================================================
   CalcNetPanel — Calculator.net styled layout
   Props:
     tabs (default US/Metric), activeTab, onTabChange, slug
     children  → LEFT input area (below the tabs, in the gray box)
     settings  → optional collapsible settings JSX
     right     → RIGHT result panel content
     belowGrid → full-width area rendered UNDER the two-column grid (for
                 large tables and category charts that shouldn't overlap the
                 result). Marked data-print="tables".
   ========================================================================== */
export function CalcNetPanel({
  tabs = ['US Units', 'Metric Units'],
  activeTab,
  onTabChange,
  bannerText = 'Modify the values above to update the result.',
  children,
  settings,
  right,
  belowGrid,
  slug = 'calc',
}) {
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div data-print-scope>
      {/* Blue instruction banner */}
      <div className="w-full mb-4 rounded-md bg-[#1e40af] text-white font-medium text-sm md:text-base px-4 py-3 flex items-center justify-center gap-3" data-print-hide>
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/95 text-[#1e40af]">
          <ChevronDown className="w-4 h-4" strokeWidth={3} />
        </span>
        {bannerText}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT — input panel */}
        <div data-print="inputs">
          <div className="flex gap-[2px] pl-0">
            {tabs.map((label, i) => {
              const isActive = activeTab === i;
              return (
                <button
                  key={label}
                  data-testid={`${slug}-tab-${i}`}
                  onClick={() => onTabChange && onTabChange(i)}
                  className={`px-4 py-2 text-sm font-semibold border border-b-0 rounded-t-md ${
                    isActive
                      ? 'bg-slate-100 text-slate-900 border-slate-300 relative z-10'
                      : 'bg-[#1e40af] text-white border-[#1e40af] hover:bg-[#1d4ed8]'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="bg-slate-100 border border-slate-300 rounded-md rounded-tl-none p-5">
            <div className="space-y-3">{children}</div>
            {settings && (
              <div className="mt-4">
                <button
                  onClick={() => setShowSettings(v => !v)}
                  className="text-[#1e40af] font-semibold text-sm underline underline-offset-2 hover:text-[#1e3a8a]"
                  data-testid={`${slug}-toggle-settings`}
                >
                  {showSettings ? '− Settings' : '+ Settings'}
                </button>
                {showSettings && (
                  <div className="mt-3 pt-3 border-t border-slate-300 space-y-3" data-testid={`${slug}-settings-panel`}>
                    {settings}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — result panel */}
        <div data-print="result">
          <div className="bg-[#5a8a3a] text-white rounded-t-md px-4 py-3 font-bold text-xl">
            <span>Result</span>
          </div>
          <div className="border border-slate-300 border-t-0 rounded-b-md p-5 bg-white">
            {right}
          </div>
        </div>
      </div>

      {/* BELOW-GRID full width area for large tables & category charts */}
      {belowGrid && (
        <div className="mt-6" data-print="tables">
          {belowGrid}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
export function Row({ label, children, suffix }) {
  return (
    <div className="grid grid-cols-[80px_1fr] md:grid-cols-[100px_1fr] items-center gap-3">
      <label className="text-slate-800 text-sm font-medium">{label}</label>
      <div className="flex items-center gap-2">{children}{suffix && <span className="text-slate-500 text-sm">{suffix}</span>}</div>
    </div>
  );
}

export function UnitInput({ value, onChange, unit, testid, className = '' }) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value === '' ? '' : +e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 pr-16 bg-white border border-slate-400 rounded-sm text-slate-900 text-sm focus:outline-none focus:border-blue-600"
      />
      {unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">{unit}</span>}
    </div>
  );
}

export function RadioGroup({ value, onChange, options, name = 'grp' }) {
  return (
    <div className="flex items-center gap-6">
      {options.map(o => (
        <label key={o.value} className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-800">
          <input type="radio" name={name} checked={value === o.value} onChange={() => onChange(o.value)}
            className="w-4 h-4 accent-[#1e40af]" />
          {o.label}
        </label>
      ))}
    </div>
  );
}

export function FeetInches({ ft, inches, onFt, onIn, testidPrefix = 'h' }) {
  return (
    <>
      <UnitInput value={ft} onChange={onFt} unit="feet" testid={`${testidPrefix}-ft`} />
      <UnitInput value={inches} onChange={onIn} unit="inches" testid={`${testidPrefix}-in`} />
    </>
  );
}

export function ResultTable({ headers, rows, testid = 'result-table' }) {
  return (
    <table className="w-full text-sm border-collapse" data-testid={testid}>
      {headers && (
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="bg-[#1e40af] text-white font-semibold px-3 py-2 text-left border border-slate-300">
                {h}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
            {r.map((c, j) => (
              <td key={j} className="px-3 py-2 border border-slate-200 text-slate-800">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* Color-band bar with a marker arrow */
export function BandMeter({ bands, value, formatValue }) {
  const min = bands[0].start;
  const max = bands[bands.length - 1].end;
  const span = max - min;
  const pct = Math.max(0, Math.min(100, ((value - min) / span) * 100));
  return (
    <div className="my-3">
      <div className="relative">
        <div className="absolute -top-6 flex flex-col items-center" style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}>
          <span className="text-xs font-semibold text-slate-800">{formatValue ? formatValue(value) : value}</span>
          <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-slate-900" />
        </div>
        <div className="w-full h-5 rounded-sm overflow-hidden flex">
          {bands.map((b, i) => (
            <div key={i} style={{ background: b.color, flex: b.end - b.start }} />
          ))}
        </div>
        <div className="flex text-[10px] mt-1 text-slate-700">
          {bands.map((b, i) => (
            <div key={i} className="text-center font-medium" style={{ flex: b.end - b.start }}>
              <div className="font-bold">{b.rangeLabel}</div>
              <div>{b.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BulletNotes({ items }) {
  return (
    <ul className="mt-4 list-disc pl-5 text-sm text-slate-700 space-y-1">
      {items.map((it, i) => (
        <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
      ))}
    </ul>
  );
}
